import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSession } from "../SessionContext";
import { handleLogout } from "../utils/authUtils"; // No changes needed here
import "../styles/Navbar.css";

const Navbar: React.FC = () => {
  const { session } = useSession();
  const { t, i18n } = useTranslation("translation");

  const [menuOpen, setMenuOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(i18n.isInitialized);

  useEffect(() => {
    const handleInitialized = () => setIsInitialized(true);
    if (!i18n.isInitialized) {
      i18n.on("initialized", handleInitialized);
    } else {
      setIsInitialized(true);
    }

    return () => {
      i18n.off("initialized", handleInitialized);
    };
  }, [i18n]);

  if (!isInitialized) {
    return null;
  }

  const translate = t as unknown as (key: string) => string;

  const toggleMenu = () => setMenuOpen((prev) => !prev);
  const toggleLanguage = () => {
    const newLang = i18n.language === "en" ? "cy" : "en";
    i18n.changeLanguage(newLang);
  };

  // ✅ Corrected Quick Exit handler
  const handleQuickExit = () => {
    console.log("Quick Exit triggered");

    // Set EmergencyExit flag
    document.cookie = "EmergencyExit=true; path=/; max-age=300";

    // Distraction URLs
    const urls = [
      "https://www.google.co.uk",
      "https://www.amazon.co.uk",
      "https://www.microsoft.com",
      "https://www.bbc.co.uk",
    ];

    let firstNewTab: Window | null = null;

    urls.forEach((url, index) => {
      const newTab = window.open(url, "_blank");
      if (newTab && index === 0) {
        firstNewTab = newTab;
      }
    });

    if (firstNewTab) {
      setTimeout(() => {
        firstNewTab?.focus();
      }, 500);
    }

    // Perform Keycloak logout with redirect to InvitePage
    const id_token = localStorage.getItem("id_token");
    const idTokenHint = id_token ? `&id_token_hint=${encodeURIComponent(id_token)}` : "";

    const keycloakLogoutUrl =
      "https://ncjp.psanalytics.co.uk/auth/realms/ncjp/protocol/openid-connect/logout" +
      "?post_logout_redirect_uri=https://ncjp.psanalytics.co.uk/invite" +
      "&client_id=ncjp-app" +
      "&prompt=none" +
      idTokenHint;

    localStorage.clear();
    sessionStorage.clear();

    setTimeout(() => {
      window.location.href = keycloakLogoutUrl;
    }, 700);
  };

  return (
    <header className="govuk-header" role="banner">
      <div className="govuk-header__container">
        <div className="govuk-header__brand">
          <img
            src="/assets/images/puk-logo.png"
            alt="GOV.UK"
            className="govuk-header__logo"
          />
          <Link to="/" className="govuk-header__link--service-name">
            {translate("portalName") || "National MyPolice Portal"}
          </Link>
        </div>

        <button
          className="govuk-header__menu-toggle"
          onClick={toggleMenu}
          aria-expanded={menuOpen}
        >
          <span className="govuk-header__menu-toggle-icon">&#9776;</span>
        </button>

        <nav
          className={`govuk-header__navigation ${menuOpen ? "govuk-header__navigation--open" : ""}`}
          aria-label="Account menu"
        >
          <ul className="govuk-header__navigation-list">
            <li className="govuk-header__navigation-item">
              <Link to="/account" className="govuk-header__link">
                {translate("manageAccount") || "Manage my account"}
              </Link>
            </li>
            <li className="govuk-header__navigation-item">
              <Link to="/preferences" className="govuk-header__link">
                {translate("preferences") || "Preferences"}
              </Link>
            </li>
            <li className="govuk-header__navigation-item">
              <Link to="/my-area" className="govuk-header__link">
              {translate("myArea") || "My Area"}
              </Link>
            </li>
            <li className="govuk-header__navigation-item">
              <button
                className="language-toggle-button"
                onClick={toggleLanguage}
                title={translate("toggleLanguage") || "Toggle Language"}
              >
                {i18n.language === "en" ? (
                  <img src="/assets/images/ukFlag.png" alt="UK Flag" className="flag-image" />
                ) : (
                  <img src="/assets/images/welshFlag.png" alt="Welsh Flag" className="flag-image" />
                )}
              </button>
            </li>
            <li className="govuk-header__navigation-item govuk-header__navigation-item--logout">
              <button
                className="govuk-button govuk-button--small"
                onClick={handleLogout}
              >
                🚪 {translate("logout") || "Logout"}
              </button>
            </li>
            <li className="govuk-header__navigation-item">
              <button
                className="govuk-button govuk-button--small govuk-button--quick-exit"
                onClick={handleQuickExit}
              >
                ⏱ {translate("quickExit") || "Quick Exit"}
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;

